/**
 * Moz V3 Per-Method-Type Row Transformers (Story 20.7, AC #4, AC #12)
 *
 * Every transformer in this module emits **lowerCamelCase** keys consistent
 * with the rest of the ProvenLeap record schema. Moz V3 returns snake_case
 * fields (e.g. `domain_authority`, `spam_score`, `link_propensity`) per the
 * `result.*.{field}` envelope shape documented at
 * moz.com/help/links-api/making-calls/url-metrics verified 2026-05-10.
 *
 * **Key normalization invariants (AC #12):**
 *
 *  1. **`spam_score: -1` sentinel** is normalized to `null` — Moz uses -1 to
 *     mean "no score available" per the V2/V3 docs. Leaking -1 into widget
 *     data would render as a real value below 0 on chart axes.
 *
 *  2. **`http_code: 0` sentinel** is preserved as 0 — Moz uses 0 to mean
 *     "never crawled" per the docs. This is a meaningful sentinel (data-table
 *     widgets show "Not crawled") so we keep it.
 *
 *  3. Each row carries a `_reportType` discriminator (one of `"site-metrics"`,
 *     `"brand-authority"`, `"top-pages"`, `"linking-root-domains"`) so the
 *     widget renderer can dispatch shape-specific layouts. Mirrors Ahrefs 20.1
 *     + Semrush 20.4 + PageSpeed `_reportType` discriminator pattern.
 *
 *  4. Each row carries `target` (the queried domain), `scope` (when applicable),
 *     and `indexId` (the Moz index snapshot identifier from
 *     `metadata.indexId.fetch`). The indexId is captured ONCE per `fetchMetrics`
 *     invocation and stamped on every record.
 *
 * **Build-time test:** `metric-allowlist.test.ts` asserts that every metric in
 * `MOZ_METRICS` has a non-empty `apiField` value AND that `apiField` appears
 * in the per-method-type field tables in `_bmad/bmm/data/integration-metrics/moz.md`.
 * A separate test asserts no Moz record key ever uses snake_case (no
 * `record.spam_score`, only `record.spamScore`).
 */

// ============================================================================
// Discriminator
// ============================================================================

export type MozReportType =
  | "site-metrics"
  | "brand-authority"
  | "top-pages"
  | "linking-root-domains"
  | "anchor-text"; // Story 35.2 — 5th discriminator; paid-tier-gated

// ============================================================================
// Field-coercion helpers
// ============================================================================

/**
 * Coerce a Moz integer field to `number | null`. Treats `null`, `undefined`,
 * empty string, and non-numeric strings as `null`.
 */
export function transformMozIntegerField(value: unknown): number | null {
  if (value === null || value === undefined) return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed.length === 0) return null;
    const parsed = Number.parseInt(trimmed, 10);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

/**
 * Coerce a Moz decimal field (e.g. `link_propensity`, 0.0-1.0) to `number | null`.
 */
export function transformMozDecimalField(value: unknown): number | null {
  if (value === null || value === undefined) return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed.length === 0) return null;
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

/**
 * Coerce a Moz string field to `string | null`. Trims; empty becomes null.
 */
export function transformMozStringField(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}

/**
 * Normalize the `spam_score` Moz field per AC #12. Moz uses -1 sentinel to
 * mean "no score available"; we surface that as `null` so chart axes don't
 * render -1 as a real value.
 */
export function transformMozSpamScore(value: unknown): number | null {
  const coerced = transformMozIntegerField(value);
  if (coerced === null) return null;
  if (coerced < 0) return null; // Moz `-1` sentinel → null
  return coerced;
}

// ============================================================================
// Site Metrics row transformer (data.site.metrics.fetch — 15 fields)
// ============================================================================

export interface MozSiteMetricsApiRow {
  readonly domain_authority?: unknown;
  readonly page_authority?: unknown;
  readonly spam_score?: unknown;
  readonly link_propensity?: unknown;
  readonly pages_to_page?: unknown;
  readonly external_pages_to_page?: unknown;
  readonly nofollow_pages_to_page?: unknown;
  readonly redirect_pages_to_page?: unknown;
  readonly deleted_pages_to_page?: unknown;
  readonly root_domains_to_page?: unknown;
  readonly nofollow_root_domains_to_page?: unknown;
  readonly pages_crawled_from_root_domain?: unknown;
  readonly root_domains_from_root_domain?: unknown;
  readonly title?: unknown;
  readonly subdomain?: unknown;
  readonly root_domain?: unknown;
  readonly last_crawled?: unknown;
  readonly http_code?: unknown;
  // Story 35.2 — subdomain-rollup wire fields. Returned unconditionally by
  // `data.site.metrics.fetch` per moz.com/help/links-api/making-calls/url-metrics
  // verified 2026-05-19/2026-05-20 (mirror confirmed all 5 fields present in
  // every response — no `cols` filter exists in V2/V3).
  readonly pages_to_subdomain?: unknown;
  readonly nofollow_pages_to_subdomain?: unknown;
  readonly redirect_pages_to_subdomain?: unknown;
  readonly external_pages_to_subdomain?: unknown;
  readonly root_domains_to_subdomain?: unknown;
}

export function transformMozSiteMetricsRow(
  row: MozSiteMetricsApiRow,
  context: { target: string; scope: string; indexId: string },
): Record<string, unknown> {
  return {
    _reportType: "site-metrics" satisfies MozReportType,
    target: context.target,
    scope: context.scope,
    indexId: context.indexId,
    domainAuthority: transformMozIntegerField(row.domain_authority),
    pageAuthority: transformMozIntegerField(row.page_authority),
    spamScore: transformMozSpamScore(row.spam_score),
    linkPropensity: transformMozDecimalField(row.link_propensity),
    pagesToPage: transformMozIntegerField(row.pages_to_page),
    externalPagesToPage: transformMozIntegerField(row.external_pages_to_page),
    nofollowPagesToPage: transformMozIntegerField(row.nofollow_pages_to_page),
    redirectPagesToPage: transformMozIntegerField(row.redirect_pages_to_page),
    deletedPagesToPage: transformMozIntegerField(row.deleted_pages_to_page),
    rootDomainsToPage: transformMozIntegerField(row.root_domains_to_page),
    nofollowRootDomainsToPage: transformMozIntegerField(
      row.nofollow_root_domains_to_page,
    ),
    pagesCrawledFromRootDomain: transformMozIntegerField(
      row.pages_crawled_from_root_domain,
    ),
    rootDomainsFromRootDomain: transformMozIntegerField(
      row.root_domains_from_root_domain,
    ),
    title: transformMozStringField(row.title),
    subdomain: transformMozStringField(row.subdomain),
    rootDomain: transformMozStringField(row.root_domain),
    lastCrawled: transformMozStringField(row.last_crawled),
    httpCode: transformMozIntegerField(row.http_code),
    // Story 35.2 — subdomain-rollup fields. Zero new JSON-RPC call cost
    // (returned by data.site.metrics.fetch unconditionally). Cross-tool widget
    // (Story 20.9) treats `pagesToSubdomain` as the Moz analogue of Ahrefs
    // `liveBacklinks` + Semrush `backlinks` when the target is a subdomain.
    pagesToSubdomain: transformMozIntegerField(row.pages_to_subdomain),
    nofollowPagesToSubdomain: transformMozIntegerField(row.nofollow_pages_to_subdomain),
    redirectPagesToSubdomain: transformMozIntegerField(row.redirect_pages_to_subdomain),
    externalPagesToSubdomain: transformMozIntegerField(row.external_pages_to_subdomain),
    rootDomainsToSubdomain: transformMozIntegerField(row.root_domains_to_subdomain),
  };
}

// ============================================================================
// Brand Authority row transformer (data.site.metrics.brand.authority.fetch — 1 field)
// ============================================================================

export interface MozBrandAuthorityApiRow {
  readonly brand_authority_score?: unknown;
  readonly query?: unknown;
}

export function transformMozBrandAuthorityRow(
  row: MozBrandAuthorityApiRow,
  context: { target: string; indexId: string },
): Record<string, unknown> {
  return {
    _reportType: "brand-authority" satisfies MozReportType,
    target: context.target,
    indexId: context.indexId,
    brandAuthorityScore: transformMozIntegerField(row.brand_authority_score),
    query: transformMozStringField(row.query) ?? context.target,
  };
}

// ============================================================================
// Top Pages row transformer (data.site.top-pages.list — 8 fields)
// ============================================================================

export interface MozTopPagesApiRow {
  readonly page?: unknown;
  readonly page_authority?: unknown;
  readonly pages_to_page?: unknown;
  readonly external_pages_to_page?: unknown;
  readonly root_domains_to_page?: unknown;
  readonly title?: unknown;
  readonly http_code?: unknown;
  readonly last_crawled?: unknown;
  // Story 35.2 — per-page extended wire fields. Returned unconditionally by
  // `data.site.top-pages.list` per moz.com/help/links-api/making-calls/top-pages
  // verified 2026-05-19/2026-05-20.
  readonly nofollow_pages_to_page?: unknown;
  readonly redirect_pages_to_page?: unknown;
  readonly deleted_pages_to_page?: unknown;
  readonly external_nofollow_pages_to_page?: unknown;
  readonly indirect_root_domains_to_page?: unknown;
}

export function transformMozTopPagesRow(
  row: MozTopPagesApiRow,
  context: { target: string; scope: string; indexId: string },
): Record<string, unknown> {
  return {
    _reportType: "top-pages" satisfies MozReportType,
    target: context.target,
    scope: context.scope,
    indexId: context.indexId,
    topPagePageUrl: transformMozStringField(row.page),
    topPagePageAuthority: transformMozIntegerField(row.page_authority),
    topPagePagesToPage: transformMozIntegerField(row.pages_to_page),
    topPageExternalPagesToPage: transformMozIntegerField(
      row.external_pages_to_page,
    ),
    topPageRootDomainsToPage: transformMozIntegerField(row.root_domains_to_page),
    topPageTitle: transformMozStringField(row.title),
    topPageHttpCode: transformMozIntegerField(row.http_code),
    topPageLastCrawled: transformMozStringField(row.last_crawled),
    // Story 35.2 — per-page extended fields. Zero new JSON-RPC call cost.
    topPageNofollowPagesToPage: transformMozIntegerField(row.nofollow_pages_to_page),
    topPageRedirectPagesToPage: transformMozIntegerField(row.redirect_pages_to_page),
    topPageDeletedPagesToPage: transformMozIntegerField(row.deleted_pages_to_page),
    topPageExternalNofollowPagesToPage: transformMozIntegerField(
      row.external_nofollow_pages_to_page,
    ),
    topPageIndirectRootDomainsToPage: transformMozIntegerField(
      row.indirect_root_domains_to_page,
    ),
  };
}

// ============================================================================
// Linking Root Domains row transformer (data.site.linking-root-domains.list — 6 fields)
// ============================================================================

export interface MozLinkingRootDomainsApiRow {
  readonly linking_root_domain?: unknown;
  readonly source_domain_authority?: unknown;
  readonly link_propensity?: unknown;
  readonly spam_score?: unknown;
  readonly nofollow_pages?: unknown;
  readonly redirect_pages?: unknown;
  // Story 35.2 — per-linker extended wire fields. Returned by
  // `data.site.linking-root-domains.list` per
  // moz.com/help/links-api/making-calls/linking-root-domains verified
  // 2026-05-19/2026-05-20. `last_crawled` + `first_seen` are ISO date strings
  // (YYYY-MM-DD); `link_pattern` is a categorical string (e.g. `"text"`,
  // `"image"`, `"redirect"`).
  readonly deleted_pages?: unknown;
  readonly last_crawled?: unknown;
  readonly first_seen?: unknown;
  readonly link_pattern?: unknown;
}

export function transformMozLinkingRootDomainsRow(
  row: MozLinkingRootDomainsApiRow,
  context: { target: string; scope: string; indexId: string },
): Record<string, unknown> {
  return {
    _reportType: "linking-root-domains" satisfies MozReportType,
    target: context.target,
    scope: context.scope,
    indexId: context.indexId,
    linkingRootDomain: transformMozStringField(row.linking_root_domain),
    sourceDomainAuthority: transformMozIntegerField(row.source_domain_authority),
    linkerLinkPropensity: transformMozDecimalField(row.link_propensity),
    linkerSpamScore: transformMozSpamScore(row.spam_score),
    nofollowPagesPerLinker: transformMozIntegerField(row.nofollow_pages),
    redirectPagesPerLinker: transformMozIntegerField(row.redirect_pages),
    // Story 35.2 — per-linker extended fields.
    deletedPagesPerLinker: transformMozIntegerField(row.deleted_pages),
    linkerLastCrawled: transformMozStringField(row.last_crawled),
    linkerFirstSeen: transformMozStringField(row.first_seen),
    linkerLinkPattern: transformMozStringField(row.link_pattern),
  };
}

// ============================================================================
// Story 35.2 — Anchor Text row transformer (data.site.anchor-text.list — 3 wire fields)
// ============================================================================

/**
 * Anchor Text row interface (3 wire-format fields per Moz V3 docs verified
 * 2026-05-19/2026-05-20 — see
 * moz.com/help/links-api/making-calls/anchor-text).
 *
 * **PAID-TIER ONLY.** The `data.site.anchor-text.list` method requires a Moz
 * Standard plan or higher. Free / Starter plans receive a 403 with an error
 * message/code containing "plan" / "subscription" / "tier" / "entitle"
 * substrings (Moz's exact error envelope is variable). The orchestrator at
 * `moz.ts` runs a lazy first-call entitlement probe and flips
 * `metadata.anchorTextEntitled` accordingly; this transformer is only
 * reached on entitled tenants.
 *
 * **HTML-escape contract (Story 20.8 Round 3 Red Team Attack #1 carry-forward):**
 * `anchor_text` values from Moz are arbitrary user-controlled content (the
 * actual anchor text strings that referring pages use to link to the target).
 * This transformer preserves the raw value; the RENDERER OR FIXTURE GENERATOR
 * applies HTML-escaping at the display boundary. Do NOT escape here — it
 * would double-escape in the renderer.
 *
 * **Method-name pre-listed in `MOZ_METHOD_NAMES`** at
 * `packages/integrations/src/moz/json-rpc.ts` since Story 20.7. Story 35.2
 * activates the method without re-touching the enum.
 */
export interface MozAnchorTextApiRow {
  readonly anchor_text?: unknown;
  readonly external_pages?: unknown;
  readonly external_root_domains?: unknown;
}

export function transformMozAnchorTextRow(
  row: MozAnchorTextApiRow,
  context: { target: string; scope: string; indexId: string },
): Record<string, unknown> {
  return {
    _reportType: "anchor-text" satisfies MozReportType,
    target: context.target,
    scope: context.scope,
    indexId: context.indexId,
    anchorTextTerm: transformMozStringField(row.anchor_text),
    anchorTextExternalPages: transformMozIntegerField(row.external_pages),
    anchorTextExternalRootDomains: transformMozIntegerField(
      row.external_root_domains,
    ),
  };
}
